import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { PublishFlow } from '@/components/admin/publish-flow';
import { Pager } from '@/components/pager';
import { GarmentMockup } from '@/components/garment-mockup';
import { Badge, EmptyState, buttonClass, inputClass } from '@/components/ui';
import { accentTextStyle } from '@/lib/accent';
import type { GarmentType } from '@/lib/types';
import { formatCents } from '@/lib/money';
import { useSuspenseQuery } from '@tanstack/react-query';

import { adminProductListingQuery, adminProductsQuery, adminPublishedQuery } from '@/queries';
import { DownloadCsv } from '@/components/admin/download-csv';

/**
 * Catalogue management. AC-5.2.
 *
 * Three things live here: the blanks, the published retail products, and the
 * publish flow that composes one from the other. The publish preview uses the
 * exact mockup component the storefront renders, so what an operator approves
 * here is what a shopper sees.
 */

/**
 * Where a shop row is changed. Spec 014, FR-1423.
 *
 * This screen had a price box and a switch on every row (R-269). The style
 * page had the same two, and the stock item panel now has them as well, so
 * the same control lived in three places. Guy, 29 October 2026 (Part 2.5):
 * Stock on hand is the main screen. So each control has one home, the style
 * page and the panel, and a row here says where the row stands and opens the
 * style page, where both controls are.
 */
function PublishedRowLink({ row }: { row: { active: boolean; product_id: string | null } }) {
  return (
    <span className="flex items-center justify-end gap-2">
      <Badge tone={row.active ? 'ok' : 'neutral'}>{row.active ? 'In the shop' : 'Switched off'}</Badge>
      {row.product_id && (
        <Link
          to="/admin/styles/$productId"
          params={{ productId: row.product_id }}
          className="text-[12.5px] font-semibold underline"
        >
          Change
        </Link>
      )}
    </span>
  );
}

export default function AdminProductsPage() {
  const { brands, products: blanks, designs, methods } = useSuspenseQuery(adminProductsQuery).data;

  /**
   * The published table is read SEPARATELY, and paged. R-264.
   *
   * It used to come through the catalogue helper with everything else, which
   * meant 676 rows rendered at once under a heading that said "Published
   * products" and no number anywhere. At 901 it would have quietly stopped
   * being all of them, because the helper's ceiling is 900 and a truncated list
   * looks exactly like a complete one.
   *
   * The publish flow still takes the blanks, the designs and the methods from
   * the query above: it needs the whole set to offer a choice, and those are
   * bounded by what the studio sells rather than by what it has ever sold.
   */
  const [page, setPage] = useState(1);
  const [state, setState] = useState('all');
  const [query, setQuery] = useState('');
  const [needle, setNeedle] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setNeedle(query), 250);
    return () => clearTimeout(t);
  }, [query]);
  useEffect(() => { setPage(1); }, [needle, state]);

  const { data, isFetching } = useQuery({
    ...adminPublishedQuery({ page, q: needle, state }),
    placeholderData: keepPreviousData,
  });

  /**
   * The blanks list is paged too. R-264.
   *
   * 381 cards in one grid, under a heading with no number on it. The publish
   * flow above still takes the whole set from `adminProductsQuery`, because a
   * picker that offers a page of choices is a picker that hides the rest.
   * Listing them and choosing between them are different jobs.
   */
  const [blankPage, setBlankPage] = useState(1);
  const { data: blankData, isFetching: blanksBusy } = useQuery({
    ...adminProductListingQuery({ page: blankPage }),
    placeholderData: keepPreviousData,
  });

  // The handler selects `colors` and `sizes` alongside the listing columns, so
  // the rows carry what the card draws even though `ProductListing` does not
  // name them.
  const blankRows = (blankData?.rows ?? []) as unknown as typeof blanks;

  const published = (data?.rows ?? []) as Array<{
    id: string; title: string; brand_id: string; product_id: string | null; type: GarmentType;
    color_name: string; color_hex: string; design_url: string | null;
    method_key: string; colours: number; locations: string[];
    pos_x: number | null; pos_y: number | null; scale: number | null;
    base_cost_cents: number; retail_price_cents: number;
    fulfilment: string; active: boolean;
    brands: { name: string; slug: string; accent_hex: string | null } | null;
  }>;

  return (
    <div>
      <div className="mb-6">
        {/* "Publishing", because that is what the menu item says. It read
            "Catalogue", so clicking Publishing landed you on a page with a
            different name at the top, and the menu has a separate "Products"
            entry besides. Two names for one screen is how somebody decides they
            are in the wrong place. R-190. */}
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Publishing</h1>
        <p className="mt-1 text-[12.5px] text-muted">
          Blanks, published products and the publish flow.
        </p>
      </div>

      {/* R-172. Guy opens this six days before the trade show and the honest
          answer is "the export has not arrived", not an empty grid. */}
      {blanks.length === 0 && (data?.total ?? 0) === 0 && (
        <EmptyState
          title="The catalogue has not been loaded yet"
          body="Export the products from Shopify (Products, Export, All products, CSV
                for Excel) and send the file. It loads with one command, and the shop
                fills the moment it does. Until then the storefront renders in your
                colours with nothing in it."
        />
      )}

      <PublishFlow brands={brands} blanks={blanks} designs={designs} methods={methods} />

      <section className="mt-12" aria-labelledby="published-heading">
        <h2 id="published-heading" className="mb-1 text-lg font-bold tracking-[-0.02em]">
          Published products
        </h2>
        <p className="mb-4 text-[12.5px] text-muted">
          To change a price or take a colour out of the shop, open its style with Change, or open
          the stock item on Stock on hand.
        </p>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          {[
            { key: 'all', label: 'All' },
            { key: 'live', label: `In the shop${data ? ` (${data.live})` : ''}` },
            { key: 'off', label: 'Switched off' },
          ].map((f) => (
            <button
              key={f.key}
              type="button"
              className={buttonClass(state === f.key ? 'primary' : 'secondary', 'sm')}
              onClick={() => setState(f.key)}
            >
              {f.label}
            </button>
          ))}
          <input
            className={`${inputClass} ml-auto w-[240px]`}
            placeholder="Search by title"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search published products"
          />
          <DownloadCsv
            path="/api/published/export"
            params={{ q: needle.trim(), state: state === 'all' ? undefined : state }}
          />
        </div>

        <div className="overflow-x-auto rounded-[10px] border border-line bg-panel">
          <table className="dt w-full min-w-[860px] text-left">
            <thead>
              <tr>
                <th scope="col">Product</th>
                <th scope="col">Brand</th>
                <th scope="col">Fulfilment</th>
                <th scope="col" className="num">Cost</th>
                <th scope="col" className="num">Retail</th>
                <th scope="col" className="num">Margin</th>
                <th scope="col" className="num">In the shop</th>
              </tr>
            </thead>
            <tbody>
              {published.map((p) => {
                const margin = p.retail_price_cents - p.base_cost_cents;
                /**
                 * Null, not NaN. R-268.
                 *
                 * A gift card and a size guide are published rows with a retail
                 * price of zero, so this divided by zero and the margin column
                 * read "NaN%" on the live screen. There is no margin on a thing
                 * that is not sold for money, and a dash says that. "NaN" says
                 * the system is broken, which is how somebody stops trusting
                 * every other number in the row.
                 */
                const pct = p.retail_price_cents > 0
                  ? Math.round((margin / p.retail_price_cents) * 100)
                  : null;
                return (
                  <tr
                    key={p.id}
                    className={'border-b border-line last:border-b-0 '
                      + (p.active ? '' : 'opacity-60')}
                  >
                <td>
                      <div className="flex items-center gap-3">
                        <div className="w-12 shrink-0 rounded-[8px] bg-raised p-1">
                          <GarmentMockup
                            type={p.type}
                            colorHex={p.color_hex}
                            designUrl={p.design_url}
                            placement={{
                              pos_x: p.pos_x ?? 50,
                              pos_y: p.pos_y ?? 48,
                              scale: p.scale ?? 60,
                            }}
                            label={p.title}
                            className="aspect-square w-full"
                          />
                        </div>
                        <span>
                          {/* The title opens the style: every colour, its
                              price and its stock by size, in one place. Spec
                              010, tracker #10. */}
                          {p.product_id ? (
                            <Link
                              to="/admin/styles/$productId"
                              params={{ productId: p.product_id }}
                              className="block font-semibold hover:underline"
                            >
                              {p.title}
                            </Link>
                          ) : (
                            <span className="block font-semibold">{p.title}</span>
                          )}
                          <span className="text-sm text-muted">
                            {p.color_name} &middot; {p.method_key}
                          </span>
                        </span>
                      </div>
                    </td>
                <td>
                      <span
                        className="accent-text font-semibold"
                        style={accentTextStyle(p.brands?.accent_hex ?? '#111111')}
                     >
                        {p.brands?.name ?? 'no brand'}
                      </span>
                    </td>
                <td>
                      {/* No quantity here. `published_products.stock_qty` is a
                          number somebody typed once and nothing updates, so the
                          shop reads the ledger instead (R-154). Printing the
                          stale one on an operations screen would be a second
                          answer to "how many are there", and the wrong one.
                          Stock on hand is the screen that knows. */}
                      {p.fulfilment === 'stock' ? (
                        <Badge tone="neutral">Stock item</Badge>
                      ) : (
                        <Badge tone="neutral">Print on demand</Badge>
                      )}
                    </td>
                <td className="num font-mono text-muted">
                      {formatCents(p.base_cost_cents)}
                    </td>
                <td className="num font-mono font-bold">
                      {formatCents(p.retail_price_cents)}
                    </td>
                <td className="num font-mono">
                      {formatCents(margin)}
                      <span className="ml-1.5 text-[13px] text-muted">
                        {pct === null ? '\u2014' : `${pct}%`}
                      </span>
                    </td>
                <td className="num">
                      <PublishedRowLink row={p} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <Pager
            page={data?.page ?? 1}
            pages={data?.pages ?? 1}
            total={data?.total ?? 0}
            limit={data?.limit ?? 50}
            noun="published product"
            busy={isFetching}
            onPage={setPage}
          />
        </div>
      </section>

      <section className="mt-12" aria-labelledby="blanks-heading">
        <h2 id="blanks-heading" className="mb-4 text-lg font-bold tracking-[-0.02em]">
          Blanks
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {blankRows.map((b) => (
            <div key={b.id} className="rounded-[10px] border border-line bg-panel p-4">
              <div className="flex items-start gap-3">
                <div className="w-16 shrink-0 rounded-[8px] bg-raised p-1">
                  <GarmentMockup
                    type={b.type}
                    colorHex={b.colors[0]!.hex}
                    label={b.name}
                    className="aspect-square w-full"
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-[15px] font-bold leading-snug">{b.name}</p>
                  <p className="mt-0.5 text-sm text-muted">{b.category}</p>
                  <p className="mt-1 font-mono text-[15px] font-bold">
                    {formatCents(b.base_price_cents)}
                  </p>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {b.colors.map((c) => (
                  <span
                    key={c.name}
                    title={c.name}
                    className="size-5 rounded-full border border-ink/20"
                    style={{ background: c.hex }}
                 >
                    <span className="sr-only">{c.name}</span>
                  </span>
                ))}
              </div>
              <p className="mt-2 text-[13px] text-muted">{b.sizes.join(', ')}</p>
            </div>
          ))}
        </div>

        <Pager
          page={blankData?.page ?? 1}
          pages={blankData?.pages ?? 1}
          total={blankData?.total ?? 0}
          limit={blankData?.limit ?? 50}
          noun="blank"
          busy={blanksBusy}
          onPage={setBlankPage}
        />
      </section>
    </div>
  );
}
